import React from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export type VisibilityState = 'visible-editable' | 'visible-readonly' | 'hidden';

interface RoleVisibilitySelectorProps {
  state: VisibilityState;
  onStateChange: (state: VisibilityState) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

/**
 * Dropdown selector for role visibility with three states:
 * 1. Visible + Editable (Eye icon)
 * 2. Visible + Read-Only (Lock icon) 
 * 3. Hidden (EyeOff icon)
 * 
 * Displays the current state as text and allows selection via dropdown to prevent accidental changes.
 */
export function RoleVisibilitySelector({
  state,
  onStateChange,
  disabled = false,
  size = 'md',
  showText = true,
  className,
}: RoleVisibilitySelectorProps) {
  const getStateConfig = (visibilityState: VisibilityState) => {
    switch (visibilityState) {
      case 'visible-editable':
        return {
          icon: Eye,
          color: 'text-green-600',
          bgColor: 'bg-green-50 hover:bg-green-100',
          label: 'Visible & Editable',
          shortLabel: 'Editable',
        };
      case 'visible-readonly':
        return {
          icon: Lock,
          color: 'text-amber-600',
          bgColor: 'bg-amber-50 hover:bg-amber-100',
          label: 'Visible & Read-Only',
          shortLabel: 'Read-Only',
        };
      case 'hidden':
        return {
          icon: EyeOff,
          color: 'text-gray-400',
          bgColor: 'bg-gray-50 hover:bg-gray-100',
          label: 'Hidden',
          shortLabel: 'Hidden',
        };
    }
  };

  const currentConfig = getStateConfig(state);
  const Icon = currentConfig.icon;

  const iconSizes = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const paddingSizes = {
    sm: 'px-2 py-1',
    md: 'px-3 py-1.5',
    lg: 'px-4 py-2',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 transition-colors',
            paddingSizes[size],
            currentConfig.bgColor,
            className
          )}
        >
          <Icon className={cn(iconSizes[size], currentConfig.color)} />
          {showText && (
            <span className={cn(textSizes[size], currentConfig.color, 'font-medium')}>
              {currentConfig.shortLabel}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={() => onStateChange('visible-editable')}
          className={cn(
            'flex items-center gap-2 cursor-pointer',
            state === 'visible-editable' && 'bg-green-50'
          )}
        >
          <Eye className="h-4 w-4 text-green-600" />
          <div className="flex flex-col">
            <span className="font-medium text-green-700">Visible & Editable</span>
            <span className="text-xs text-gray-500">Users can view and edit</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onStateChange('visible-readonly')}
          className={cn(
            'flex items-center gap-2 cursor-pointer',
            state === 'visible-readonly' && 'bg-amber-50'
          )}
        >
          <Lock className="h-4 w-4 text-amber-600" />
          <div className="flex flex-col">
            <span className="font-medium text-amber-700">Visible & Read-Only</span>
            <span className="text-xs text-gray-500">Users can view but not edit</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onStateChange('hidden')}
          className={cn(
            'flex items-center gap-2 cursor-pointer',
            state === 'hidden' && 'bg-gray-50'
          )}
        >
          <EyeOff className="h-4 w-4 text-gray-400" />
          <div className="flex flex-col">
            <span className="font-medium text-gray-700">Hidden</span>
            <span className="text-xs text-gray-500">Users cannot see this</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Helper function to determine visibility state from role arrays
 */
export function getVisibilityState(
  roleId: string | null,
  hiddenForRoles?: string[],
  readOnlyForRoles?: string[]
): VisibilityState {
  if (!roleId) {
    return 'visible-editable'; // Default state when no role selected
  }
  if (hiddenForRoles?.includes(roleId)) {
    return 'hidden';
  }
  if (readOnlyForRoles?.includes(roleId)) {
    return 'visible-readonly';
  }
  return 'visible-editable';
}

/**
 * Helper function to update role arrays based on new state
 */
export function updateRoleArraysForState(
  roleId: string,
  newState: VisibilityState,
  currentHiddenForRoles?: string[],
  currentReadOnlyForRoles?: string[]
): {
  hiddenForRoles: string[];
  readOnlyForRoles: string[];
} {
  const hidden = currentHiddenForRoles || [];
  const readOnly = currentReadOnlyForRoles || [];

  // Remove role from both arrays first
  const filteredHidden = hidden.filter(r => r !== roleId);
  const filteredReadOnly = readOnly.filter(r => r !== roleId);

  switch (newState) {
    case 'visible-editable':
      // Role not in any array (default state)
      return {
        hiddenForRoles: filteredHidden,
        readOnlyForRoles: filteredReadOnly,
      };
    case 'visible-readonly':
      // Add to readOnly array
      return {
        hiddenForRoles: filteredHidden,
        readOnlyForRoles: [...filteredReadOnly, roleId],
      };
    case 'hidden':
      // Add to hidden array
      return {
        hiddenForRoles: [...filteredHidden, roleId],
        readOnlyForRoles: filteredReadOnly,
      };
  }
}

